import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SharedTaskComponent from './SharedTaskComponent';
import {
  AcademicCapIcon,
  UserGroupIcon,
  ChartBarIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { firestore } from '../../database/firebase';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  addDoc,
  writeBatch,
} from 'firebase/firestore';
import useAuth from '../../hooks/useAuth';
import {
  HiUser,
  HiEnvelope,
  HiPhone,
  HiBuildingOffice2,
  HiCheckCircle,
  HiPlus,
  HiPencil,
  HiTrash,
  HiXMark,
  HiUserGroup,
  HiCurrencyDollar,
  HiBriefcase,
  HiAcademicCap,
  HiCalendar,
  HiClipboardDocumentList,
  HiClock,
  HiExclamationTriangle,
  HiCheckBadge,
  HiArchiveBox,
  HiMagnifyingGlass,
  HiEye,
  HiPlay,
} from 'react-icons/hi2';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const colorPalette = [
  'from-role-admin to-role-admin',
  'from-role-coach to-role-coach',
  'from-role-client to-role-client',
  'from-role-coach to-role-coach',
  'from-role-bif to-role-bif',
  'from-role-moderator to-role-moderator',
];
const bgPalette = [
  'bg-role-admin/10',
  'bg-role-coach/10',
  'bg-role-client/10',
  'bg-role-coach/10',
  'bg-role-bif/10',
  'bg-role-moderator/10',
];
const textPalette = [
  'text-gray-900',
  'text-gray-900',
  'text-gray-900',
  'text-gray-900',
  'text-gray-900',
  'text-gray-900',
];

const DashboardStats = ({ stats }) => {
  const statCards = [
    {
      title: 'Total Alumni',
      value: stats.totalAlumni,
      change: '+12%',
      icon: UsersIcon,
      color: 'from-blue-600 to-blue-700',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
    },
    {
      title: 'Active Coaches',
      value: stats.activeCoaches,
      change: '+5%',
      icon: AcademicCapIcon,
      color: 'from-indigo-600 to-indigo-700',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
    },
    {
      title: 'Active Clients',
      value: stats.activeClients,
      change: '+8%',
      icon: UserGroupIcon,
      color: 'from-emerald-600 to-emerald-700',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600',
    },
    {
      title: 'Pending Applications',
      value: stats.pendingApplications,
      change: '+15%',
      icon: DocumentTextIcon,
      color: 'from-amber-600 to-amber-700',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600',
    },
    // {
    //   title: 'BIF Applications',
    //   value: stats.bifApplications,
    //   change: '+3%',
    //   icon: CurrencyDollarIcon,
    //   color: 'from-green-600 to-green-700',
    //   bgColor: 'bg-green-50',
    //   textColor: 'text-green-600',
    // },
    {
      title: 'Active Business Advisors',
      value: stats.activeModerators,
      change: '+2%',
      icon: ShieldCheckIcon,
      color: 'from-purple-600 to-purple-700',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {statCards.map((card, index) => (
        <motion.div
          key={card.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="rounded-xl bg-white p-6 shadow-lg transition-all hover:shadow-xl"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-black">{card.title}</p>
              <p className="text-3xl font-bold text-black">{card.value}</p>
              {/* <p className="text-sm text-black/70">{card.change} from last month</p> */}
            </div>
            <div className={`rounded-lg p-3 ${card.bgColor}`}>
              <card.icon className={`h-8 w-8 ${card.textColor}`} />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Live Table Component with Filters and Search
const LiveTable = ({
  title,
  data,
  columns,
  filters,
  searchTerm,
  onSearchChange,
  onFilterChange,
  onExport,
  onView,
  onEdit,
  onDelete,
  loading,
}) => {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredData = data.filter(item => {
    const matchesSearch = Object.values(item).some(value =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesFilters = Object.entries(filters).every(
      ([key, value]) => !value || item[key] === value
    );
    return matchesSearch && matchesFilters;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    return sortDirection === 'asc'
      ? aVal.toString().localeCompare(bVal.toString())
      : bVal.toString().localeCompare(aVal.toString());
  });

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading {title}...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-6 shadow-lg"
    >
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-black">{title}</h3>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onExport(sortedData)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </motion.button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-black/40" />
          <input
            type="text"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={searchTerm}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full rounded-lg border border-black/20 py-2 pl-10 pr-4 text-black focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
          />
        </div>
        <div className="flex gap-2">
          {Object.entries(filters).map(([key, value]) => (
            <select
              key={key}
              value={value}
              onChange={e => onFilterChange(key, e.target.value)}
              className="rounded-lg border border-black/20 px-3 py-2 text-sm text-black focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
            >
              <option value="">All {key}</option>
              {Array.from(new Set(data.map(item => item[key]).filter(Boolean))).map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-black/20">
              {columns.map(column => (
                <th
                  key={column.key}
                  onClick={() => column.sortable && handleSort(column.key)}
                  className={`px-4 py-3 text-left text-sm font-medium text-black ${
                    column.sortable ? 'cursor-pointer hover:bg-black/5' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    {column.sortable && sortField === column.key && (
                      <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-sm font-medium text-black">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-black/60">
                  No {title.toLowerCase()} found
                </td>
              </tr>
            ) : (
              sortedData.map((item, index) => (
                <motion.tr
                  key={item.id || index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-black/10 hover:bg-black/5"
                >
                  {columns.map(column => (
                    <td key={column.key} className="px-4 py-3 text-sm text-black">
                      {column.render ? column.render(item[column.key], item) : item[column.key]}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {onView && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onView(item)}
                          className="rounded-lg p-2 text-black hover:bg-black/10"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </motion.button>
                      )}
                      {onEdit && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onEdit(item)}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </motion.button>
                      )}
                      {onDelete && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => onDelete(item)}
                          className="rounded-lg p-2 text-black hover:bg-black/10"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </motion.button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-black/60">
        Showing {sortedData.length} of {data.length} {title.toLowerCase()}
      </div>
    </motion.div>
  );
};

const AdminTaskManager = () => {
  const { user } = useAuth();

  return (
    <SharedTaskComponent
      userRole="admin"
      userEmail={user?.email}
      canCreate={true}
      canEdit={true}
      canDelete={true}
      canAssign={true}
      title="All Tasks (Admin View)"
    />
  );
};

const AdminCRM = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalAlumni: 0,
    activeCoaches: 0,
    activeClients: 0,
    pendingApplications: 0,
    bifApplications: 0,
    activeModerators: 0,
  });
  const [data, setData] = useState({
    alumni: [],
    applications: [],
    bifApplications: [],
    clients: [],
    coaches: [],
    moderators: [],
  });
  const [loading, setLoading] = useState(true);
  const [searchTerms, setSearchTerms] = useState({});
  const [filters, setFilters] = useState({});
  const [showAlumniModal, setShowAlumniModal] = useState(false);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [showEditAlumni, setShowEditAlumni] = useState(false);
  const [editAlumniForm, setEditAlumniForm] = useState({});
  const [showBifModal, setShowBifModal] = useState(false);
  const [selectedBif, setSelectedBif] = useState(null);
  const [showEditBif, setShowEditBif] = useState(false);
  const [editBifForm, setEditBifForm] = useState({});
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEditClient, setShowEditClient] = useState(false);
  const [editClientForm, setEditClientForm] = useState({});
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [showEditCoach, setShowEditCoach] = useState(false);
  const [editCoachForm, setEditCoachForm] = useState({});
  const [showModeratorModal, setShowModeratorModal] = useState(false);
  const [selectedModerator, setSelectedModerator] = useState(null);
  const [showEditModerator, setShowEditModerator] = useState(false);
  const [editModeratorForm, setEditModeratorForm] = useState({});
  const [assignedClients, setAssignedClients] = useState([]);
  const [assignedClientsLoading, setAssignedClientsLoading] = useState(false);
  const [assignedCoachClients, setAssignedCoachClients] = useState([]);
  const [assignedCoachClientsLoading, setAssignedCoachClientsLoading] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, item: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!user) return;

    const unsubscribeFunctions = [];

    const alumniUnsubscribe = onSnapshot(
      collection(firestore, 'alumni'),
      snapshot => {
        const alumniData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setData(prev => ({ ...prev, alumni: alumniData }));
        setStats(prev => ({ ...prev, totalAlumni: alumniData.length }));
      },
      error => {
        toast.error('Failed to fetch alumni data');
      }
    );
    unsubscribeFunctions.push(alumniUnsubscribe);

    const applicationsUnsubscribe = onSnapshot(
      collection(firestore, 'applications'),
      snapshot => {
        const applicationsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setData(prev => ({ ...prev, applications: applicationsData }));
        setStats(prev => ({
          ...prev,
          pendingApplications: applicationsData.filter(app => app.status === 'pending').length,
        }));
      },
      error => {
        toast.error('Failed to fetch applications data');
      }
    );
    unsubscribeFunctions.push(applicationsUnsubscribe);

    const bifApplicationsUnsubscribe = onSnapshot(
      collection(firestore, 'bif_applications'),
      snapshot => {
        const bifData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setData(prev => ({ ...prev, bifApplications: bifData }));
        setStats(prev => ({ ...prev, bifApplications: bifData.length }));
      },
      error => {
        toast.error('Failed to fetch BIF applications data: ');
      }
    );
    unsubscribeFunctions.push(bifApplicationsUnsubscribe);

    const clientsUnsubscribe = onSnapshot(
      collection(firestore, 'clients'),
      snapshot => {
        const clientsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setData(prev => ({ ...prev, clients: clientsData }));
        setStats(prev => ({
          ...prev,
          activeClients: clientsData.filter(client => client.status === 'active').length,
        }));
      },
      error => {
        toast.error('Failed to fetch clients data: ' + error.message);
      }
    );
    unsubscribeFunctions.push(clientsUnsubscribe);

    const coachesUnsubscribe = onSnapshot(
      collection(firestore, 'coaches'),
      snapshot => {
        const coachesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setData(prev => ({ ...prev, coaches: coachesData }));
        setStats(prev => ({
          ...prev,
          activeCoaches: coachesData.filter(coach => coach.status === 'active').length,
        }));
      },
      error => {
        toast.error('Failed to fetch coaches data: ' + error.message);
      }
    );
    unsubscribeFunctions.push(coachesUnsubscribe);

    const moderatorsUnsubscribe = onSnapshot(
      collection(firestore, 'moderator'),
      snapshot => {
        const moderatorsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setData(prev => ({ ...prev, moderators: moderatorsData }));
        setStats(prev => ({
          ...prev,
          activeModerators: moderatorsData.filter(mod => mod.status === 'active').length,
        }));
      },
      error => {
        toast.error('Failed to fetch moderators data: ' + error.message);
      }
    );
    unsubscribeFunctions.push(moderatorsUnsubscribe);

    setLoading(false);

    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [user]);

  useEffect(() => {
    if (showModeratorModal && selectedModerator?.email) {
      setAssignedClientsLoading(true);
      const q = query(
        collection(firestore, 'clients'),
        where('assignedEmail', '==', selectedModerator.email)
      );
      getDocs(q)
        .then(snapshot => {
          setAssignedClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        })
        .finally(() => setAssignedClientsLoading(false));
    } else {
      setAssignedClients([]);
    }
  }, [showModeratorModal, selectedModerator]);

  useEffect(() => {
    if (showCoachModal && selectedCoach?.email) {
      setAssignedCoachClientsLoading(true);
      const q = query(
        collection(firestore, 'clients'),
        where('assignedCoachEmail', '==', selectedCoach.email)
      );
      getDocs(q)
        .then(snapshot => {
          setAssignedCoachClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        })
        .finally(() => setAssignedCoachClientsLoading(false));
    } else {
      setAssignedCoachClients([]);
    }
  }, [showCoachModal, selectedCoach]);

  const tableConfigs = {
    alumni: {
      columns: [
        { key: 'organization', label: 'Organization', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'phone', label: 'Phone', sortable: false },
        { key: 'businessStage', label: 'Business Stage', sortable: true },
        { key: 'program', label: 'Program', sortable: true },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          render: value => (
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${
                value === 'active'
                  ? 'bg-success/10 text-success'
                  : value === 'inactive'
                    ? 'bg-error/10 text-error'
                    : 'bg-muted/10 text-muted'
              }`}
            >
              {value}
            </span>
          ),
        },
      ],
    },
    applications: {
      columns: [
        {
          key: 'businessName',
          label: 'Business Name',
          sortable: true,
          render: (value, row) => row.business?.businessName || '-',
        },
        {
          key: 'industry',
          label: 'Industry',
          sortable: true,
          render: (value, row) => row.business?.industry || '-',
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          render: value => (
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${
                value === 'approved'
                  ? 'bg-success/10 text-success'
                  : value === 'pending'
                    ? 'bg-warning/10 text-warning'
                    : value === 'rejected'
                      ? 'bg-error/10 text-error'
                      : 'bg-muted/10 text-muted'
              }`}
            >
              {value}
            </span>
          ),
        },
        { key: 'applicantId', label: 'Applicant ID', sortable: true },
        { key: 'userEmail', label: 'Email', sortable: true },
        {
          key: 'completedAt',
          label: 'Completed',
          sortable: true,
          render: value =>
            value
              ? typeof value.toDate === 'function'
                ? new Date(value.toDate()).toLocaleDateString()
                : new Date(value).toLocaleDateString()
              : 'Not completed',
        },
        // {
        //   key: 'view',
        //   label: 'View',
        //   sortable: false,
        //   render: (value, row) => (
        //     <button
        //       className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
        //       onClick={() => handleViewApplication(row)}
        //       title="View Details"
        //     >
        //       <EyeIcon className="h-4 w-4" />
        //     </button>
        //   ),
        // },
      ],
    },
    bifApplications: {
      columns: [
        { key: 'companyName', label: 'Company Name', sortable: true },
        { key: 'projectTitle', label: 'Project Title', sortable: true },
        { key: 'contactPerson', label: 'Contact Person', sortable: true },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          render: value => (
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${
                value === 'approved'
                  ? 'bg-success/10 text-success'
                  : value === 'pending'
                    ? 'bg-warning/10 text-warning'
                    : value === 'rejected'
                      ? 'bg-error/10 text-error'
                      : 'bg-muted/10 text-muted'
              }`}
            >
              {value}
            </span>
          ),
        },
        {
          key: 'requestedAmount',
          label: 'Requested Amount',
          sortable: true,
          render: value => (value ? `$${value.toLocaleString()}` : 'N/A'),
        },
        { key: 'jobCreation', label: 'Jobs Created', sortable: true },
      ],
    },
    clients: {
      columns: [
        { key: 'firstname', label: 'First Name', sortable: true },
        { key: 'lastname', label: 'Last Name', sortable: true },
        { key: 'organization', label: 'Organization', sortable: true },
        { key: 'assignedEmail', label: 'Assigned To', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          render: value => (
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${
                value === 'active'
                  ? 'bg-success/10 text-success'
                  : value === 'inactive'
                    ? 'bg-error/10 text-error'
                    : 'bg-muted/10 text-muted'
              }`}
            >
              {value}
            </span>
          ),
        },
      ],
    },
    coaches: {
      columns: [
        { key: 'organization', label: 'Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'role', label: 'Role', sortable: true },
        { key: 'specialization', label: 'Specialization', sortable: true },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          render: value => (
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${
                value === 'active'
                  ? 'bg-success/10 text-success'
                  : value === 'inactive'
                    ? 'bg-error/10 text-error'
                    : 'bg-muted/10 text-muted'
              }`}
            >
              {value}
            </span>
          ),
        },
      ],
    },
    moderators: {
      columns: [
        { key: 'firstname', label: 'First Name', sortable: true },
        { key: 'lastname', label: 'Last Name', sortable: true },
        { key: 'company', label: 'Company', sortable: true },
        { key: 'department', label: 'Department', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
          render: value => (
            <span
              className={`rounded-full px-2 py-1 text-xs font-medium ${
                value === 'active'
                  ? 'bg-success/10 text-success'
                  : value === 'inactive'
                    ? 'bg-error/10 text-error'
                    : 'bg-muted/10 text-muted'
              }`}
            >
              {value}
            </span>
          ),
        },
      ],
    },
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]).filter(key => key !== 'id');
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers
          .map(header => {
            const value = row[header];
            return typeof value === 'string' && value.includes(',') ? `"${value}"` : value || '';
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`${filename} exported successfully`);
  };

  // Action handlers
  const handleView = item => {
    toast.info(
      `Viewing details for ${item.firstname || item.businessName || item.companyName || item.name}`
    );
  };

  const handleEdit = item => {
    toast.info(`Editing ${item.firstname || item.businessName || item.companyName || item.name}`);
  };

  const handleDeleteClick = item => {
    setDeleteConfirm({
      isOpen: true,
      type: 'generic',
      item,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.item || !deleteConfirm.type) return;

    setIsDeleting(true);
    try {
      switch (deleteConfirm.type) {
        case 'alumni':
          await deleteDoc(doc(firestore, 'alumni', deleteConfirm.item.id));
          setData(prev => ({
            ...prev,
            alumni: prev.alumni.filter(a => a.id !== deleteConfirm.item.id),
          }));
          setShowAlumniModal(false);
          toast.success('Alumni deleted successfully');
          break;
        case 'bif':
          await deleteDoc(doc(firestore, 'bif_applications', deleteConfirm.item.id));
          setData(prev => ({
            ...prev,
            bifApplications: prev.bifApplications.filter(b => b.id !== deleteConfirm.item.id),
          }));
          setShowBifModal(false);
          toast.success('BIF application deleted successfully');
          break;
        case 'client':
          await deleteDoc(doc(firestore, 'clients', deleteConfirm.item.id));
          setData(prev => ({
            ...prev,
            clients: prev.clients.filter(c => c.id !== deleteConfirm.item.id),
          }));
          setShowClientModal(false);
          toast.success('Client deleted successfully');
          break;
        case 'coach':
          await deleteDoc(doc(firestore, 'coaches', deleteConfirm.item.id));
          setData(prev => ({
            ...prev,
            coaches: prev.coaches.filter(c => c.id !== deleteConfirm.item.id),
          }));
          setShowCoachModal(false);
          toast.success('Coach deleted successfully');
          break;
        case 'moderator':
          await deleteDoc(doc(firestore, 'moderator', deleteConfirm.item.id));
          setData(prev => ({
            ...prev,
            moderators: prev.moderators.filter(m => m.id !== deleteConfirm.item.id),
          }));
          setShowModeratorModal(false);
          toast.success('Moderator deleted successfully');
          break;
        case 'generic':
          toast.success('Item deleted successfully');
          break;
        default:
          toast.error('Unknown delete type');
      }
      setDeleteConfirm({ isOpen: false, type: null, item: null });
    } catch (error) {
      toast.error(`Failed to delete: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, type: null, item: null });
  };

  const handleViewAlumni = alumni => {
    setSelectedAlumni(alumni);
    setShowAlumniModal(true);
  };
  const handleEditAlumni = alumni => {
    setEditAlumniForm(alumni);
    setShowEditAlumni(true);
  };
  const handleDeleteAlumni = async alumni => {
    setDeleteConfirm({
      isOpen: true,
      type: 'alumni',
      item: alumni,
    });
  };
  const handleCreateAlumni = async () => {};
  const handleUpdateAlumni = async () => {};

  const handleViewBif = bif => {
    setSelectedBif(bif);
    setShowBifModal(true);
  };
  const handleEditBif = bif => {
    setEditBifForm(bif);
    setShowEditBif(true);
  };
  const handleDeleteBif = async bif => {
    setDeleteConfirm({
      isOpen: true,
      type: 'bif',
      item: bif,
    });
  };
  const handleCreateBif = async () => {};
  const handleUpdateBif = async () => {};

  const handleViewClient = client => {
    setSelectedClient(client);
    setShowClientModal(true);
  };
  const handleEditClient = client => {
    setEditClientForm(client);
    setShowEditClient(true);
  };
  const handleDeleteClient = async client => {
    setDeleteConfirm({
      isOpen: true,
      type: 'client',
      item: client,
    });
  };
  const handleCreateClient = async () => {};
  const handleUpdateClient = async () => {};

  const handleViewCoach = coach => {
    setSelectedCoach(coach);
    setShowCoachModal(true);
  };
  const handleEditCoach = coach => {
    setEditCoachForm(coach);
    setShowEditCoach(true);
  };
  const handleDeleteCoach = async coach => {
    setDeleteConfirm({
      isOpen: true,
      type: 'coach',
      item: coach,
    });
  };
  const handleCreateCoach = async () => {};
  const handleUpdateCoach = async () => {};

  const handleViewModerator = moderator => {
    setSelectedModerator(moderator);
    setShowModeratorModal(true);
  };
  const handleEditModerator = moderator => {
    setEditModeratorForm(moderator);
    setShowEditModerator(true);
  };
  const handleDeleteModerator = async moderator => {
    setDeleteConfirm({
      isOpen: true,
      type: 'moderator',
      item: moderator,
    });
  };
  const handleCreateModerator = async () => {};
  const handleUpdateModerator = async () => {};

  const handleViewApplication = application => {
    setSelectedApplication(application);
    setShowApplicationModal(true);
  };

  const tabs = [
    { id: 'overview', name: 'Dashboard Overview', icon: ChartBarIcon },
    { id: 'alumni', name: 'Alumni', icon: UsersIcon },
    { id: 'applications', name: 'Applications', icon: DocumentTextIcon },
    { id: 'clients', name: 'Clients', icon: UserGroupIcon },
    { id: 'coaches', name: 'Coaches', icon: AcademicCapIcon },
    { id: 'moderators', name: 'Business Advisors', icon: ShieldCheckIcon },
    { id: 'tasks', name: 'Task Management', icon: HiClipboardDocumentList },
  ];

  const Detail = ({ label, value, icon }) => (
    <div className="flex items-center gap-3 py-1">
      {icon && <span className="text-blue-500">{icon}</span>}
      <div className="flex flex-col">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <span className="min-w-0 rounded border border-gray-100 bg-gray-50 px-3 py-1 text-sm text-black sm:min-w-[120px]">
          {value || '-'}
        </span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg-main p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-black">
            Admin Customer Relationship Management
          </h1>
        </div>

        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <DashboardStats stats={stats} />
          </motion.div>
        )}

        <div className="mb-6 flex flex-wrap gap-2">
          {tabs.map(tab => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'border border-black/20 bg-white text-black hover:bg-black/5 hover:text-black'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.name}
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl bg-white p-6 shadow-lg"
                >
                  <h3 className="mb-4 text-xl font-semibold text-black">Recent Activity</h3>
                  <div className="space-y-4">
                    {data.applications.slice(0, 5).map((app, index) => (
                      <div
                        key={app.id}
                        className="flex items-center gap-4 rounded-lg border border-black/10 p-4"
                      >
                        <div className="rounded-full bg-blue-50 p-2">
                          <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-black">{app.businessName}</p>
                          <p className="text-sm text-black/60">Application submitted</p>
                        </div>
                        <span className="text-sm text-black/60">
                          {app.completedAt
                            ? typeof app.completedAt.toDate === 'function'
                              ? new Date(app.completedAt.toDate()).toLocaleDateString()
                              : new Date(app.completedAt).toLocaleDateString()
                            : 'Pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            )}

            {activeTab === 'alumni' && (
              <>
                <LiveTable
                  title="Alumni Management"
                  data={data.alumni}
                  columns={tableConfigs.alumni.columns}
                  filters={{ program: filters.program || '', status: filters.status || '' }}
                  searchTerm={searchTerms.alumni || ''}
                  onSearchChange={value => setSearchTerms(prev => ({ ...prev, alumni: value }))}
                  onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
                  onExport={data => exportToCSV(data, 'alumni')}
                  onView={handleViewAlumni}
                  onEdit={handleEditAlumni}
                  onDelete={handleDeleteAlumni}
                  loading={loading}
                />
                <AnimatePresence>
                  {showAlumniModal && selectedAlumni && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
                      >
                        <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-sm">
                              {(selectedAlumni.organization ||
                                selectedAlumni.email ||
                                selectedAlumni.id)?.[0]?.toUpperCase() || 'A'}
                            </div>
                            <div>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-base font-semibold text-black">
                                  {selectedAlumni.organization || '-'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {selectedAlumni.email}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                            onClick={() => setShowAlumniModal(false)}
                            aria-label="Close"
                          >
                            <HiXMark className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="space-y-8 p-6">
                          <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                            Alumni Details
                          </div>
                          <div className="grid grid-cols-1 gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUser className="h-4 w-4 text-blue-500" /> First Name:{' '}
                                <span className="font-semibold text-black">
                                  {selectedAlumni.firstname || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUser className="h-4 w-4 text-blue-500" /> Last Name:{' '}
                                <span className="font-semibold text-black">
                                  {selectedAlumni.lastname || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiEnvelope className="h-4 w-4 text-blue-500" /> Email:{' '}
                                <span className="font-semibold text-black">
                                  {selectedAlumni.email || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiPhone className="h-4 w-4 text-blue-500" /> Phone:{' '}
                                <span className="font-semibold text-black">
                                  {selectedAlumni.phone || '-'}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBuildingOffice2 className="h-4 w-4 text-blue-500" />{' '}
                                Organization:{' '}
                                <span className="font-semibold text-black">
                                  {selectedAlumni.organization || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBuildingOffice2 className="h-4 w-4 text-blue-500" /> Business
                                Stage:{' '}
                                <span className="font-semibold text-black">
                                  {selectedAlumni.businessStage || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiCheckCircle className="h-4 w-4 text-blue-500" /> Status:{' '}
                                <span className="font-semibold text-black">
                                  {selectedAlumni.status || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUserGroup className="h-4 w-4 text-blue-500" /> Program:{' '}
                                <span className="font-semibold text-black">
                                  {selectedAlumni.program || '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditAlumniForm(selectedAlumni);
                                setShowEditAlumni(true);
                              }}
                              className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700"
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteAlumni(selectedAlumni)}
                              className="rounded-lg bg-red-100 px-4 py-2 text-red-800 shadow hover:bg-red-200"
                            >
                              Delete
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <AnimatePresence>
                  {showEditAlumni && editAlumniForm && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
                      >
                        <h2 className="mb-4 text-lg font-bold">Edit Alumni</h2>
                        <form
                          onSubmit={async e => {
                            e.preventDefault();
                            await updateDoc(
                              doc(firestore, 'alumni', editAlumniForm.id),
                              editAlumniForm
                            );
                            setData(prev => ({
                              ...prev,
                              alumni: prev.alumni.map(a =>
                                a.id === editAlumniForm.id ? editAlumniForm : a
                              ),
                            }));
                            setShowEditAlumni(false);
                          }}
                          className="space-y-4"
                        >
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <input
                              className="rounded border px-3 py-2"
                              value={editAlumniForm.firstname || ''}
                              onChange={e =>
                                setEditAlumniForm(f => ({ ...f, firstname: e.target.value }))
                              }
                              placeholder="First Name"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editAlumniForm.lastname || ''}
                              onChange={e =>
                                setEditAlumniForm(f => ({ ...f, lastname: e.target.value }))
                              }
                              placeholder="Last Name"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editAlumniForm.email || ''}
                              onChange={e =>
                                setEditAlumniForm(f => ({ ...f, email: e.target.value }))
                              }
                              placeholder="Email"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editAlumniForm.phone || ''}
                              onChange={e =>
                                setEditAlumniForm(f => ({ ...f, phone: e.target.value }))
                              }
                              placeholder="Phone"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editAlumniForm.organization || ''}
                              onChange={e =>
                                setEditAlumniForm(f => ({ ...f, organization: e.target.value }))
                              }
                              placeholder="Organization"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editAlumniForm.businessStage || ''}
                              onChange={e =>
                                setEditAlumniForm(f => ({ ...f, businessStage: e.target.value }))
                              }
                              placeholder="Business Stage"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editAlumniForm.status || ''}
                              onChange={e =>
                                setEditAlumniForm(f => ({ ...f, status: e.target.value }))
                              }
                              placeholder="Status"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editAlumniForm.program || ''}
                              onChange={e =>
                                setEditAlumniForm(f => ({ ...f, program: e.target.value }))
                              }
                              placeholder="Program"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setShowEditAlumni(false)}
                              className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                            >
                              Save
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}

            {activeTab === 'applications' && (
              <LiveTable
                title="Applications Management"
                data={data.applications}
                columns={tableConfigs.applications.columns}
                filters={{ status: filters.status || '', industry: filters.industry || '' }}
                searchTerm={searchTerms.applications || ''}
                onSearchChange={value => setSearchTerms(prev => ({ ...prev, applications: value }))}
                onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
                onExport={data => exportToCSV(data, 'applications')}
                onView={handleViewApplication}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                loading={loading}
              />
            )}

            {activeTab === 'bifApplications' && (
              <>
                <LiveTable
                  title="BIF Applications Management"
                  data={data.bifApplications}
                  columns={tableConfigs.bifApplications.columns}
                  filters={{ status: filters.status || '' }}
                  searchTerm={searchTerms.bifApplications || ''}
                  onSearchChange={value =>
                    setSearchTerms(prev => ({ ...prev, bifApplications: value }))
                  }
                  onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
                  onExport={data => exportToCSV(data, 'bif_applications')}
                  onView={handleViewBif}
                  onEdit={handleEditBif}
                  onDelete={handleDeleteBif}
                  loading={loading}
                />
                <AnimatePresence>
                  {showBifModal && selectedBif && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
                      >
                        <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-sm">
                              {(selectedBif.companyName || selectedBif.id)?.[0]?.toUpperCase() ||
                                'B'}
                            </div>
                            <div>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-base font-semibold text-black">
                                  {selectedBif.companyName || '-'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {selectedBif.contactPerson}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                            onClick={() => setShowBifModal(false)}
                            aria-label="Close"
                          >
                            <HiXMark className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="space-y-8 p-6">
                          <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                            BIF Application Details
                          </div>
                          <div className="grid grid-cols-1 gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBuildingOffice2 className="h-4 w-4 text-blue-500" /> Company
                                Name:{' '}
                                <span className="font-semibold text-black">
                                  {selectedBif.companyName || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBuildingOffice2 className="h-4 w-4 text-blue-500" /> Project
                                Title:{' '}
                                <span className="font-semibold text-black">
                                  {selectedBif.projectTitle || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUser className="h-4 w-4 text-blue-500" /> Contact Person:{' '}
                                <span className="font-semibold text-black">
                                  {selectedBif.contactPerson || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiCheckCircle className="h-4 w-4 text-blue-500" /> Status:{' '}
                                <span className="font-semibold text-black">
                                  {selectedBif.status || '-'}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiCurrencyDollar className="h-4 w-4 text-blue-500" /> Requested
                                Amount:{' '}
                                <span className="font-semibold text-black">
                                  {selectedBif.requestedAmount || 'N/A'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBriefcase className="h-4 w-4 text-blue-500" /> Jobs Created:{' '}
                                <span className="font-semibold text-black">
                                  {selectedBif.jobCreation || 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditBifForm(selectedBif);
                                setShowEditBif(true);
                              }}
                              className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700"
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteBif(selectedBif)}
                              className="rounded-lg bg-red-100 px-4 py-2 text-red-800 shadow hover:bg-red-200"
                            >
                              Delete
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Edit BIF Application Modal (simple form) */}
                <AnimatePresence>
                  {showEditBif && editBifForm && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
                      >
                        <h2 className="mb-4 text-lg font-bold">Edit BIF Application</h2>
                        <form
                          onSubmit={async e => {
                            e.preventDefault();
                            await updateDoc(
                              doc(firestore, 'bif_applications', editBifForm.id),
                              editBifForm
                            );
                            setData(prev => ({
                              ...prev,
                              bifApplications: prev.bifApplications.map(b =>
                                b.id === editBifForm.id ? editBifForm : b
                              ),
                            }));
                            setShowEditBif(false);
                          }}
                          className="space-y-4"
                        >
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <input
                              className="rounded border px-3 py-2"
                              value={editBifForm.companyName || ''}
                              onChange={e =>
                                setEditBifForm(f => ({ ...f, companyName: e.target.value }))
                              }
                              placeholder="Company Name"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editBifForm.projectTitle || ''}
                              onChange={e =>
                                setEditBifForm(f => ({ ...f, projectTitle: e.target.value }))
                              }
                              placeholder="Project Title"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editBifForm.contactPerson || ''}
                              onChange={e =>
                                setEditBifForm(f => ({ ...f, contactPerson: e.target.value }))
                              }
                              placeholder="Contact Person"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editBifForm.requestedAmount || ''}
                              onChange={e =>
                                setEditBifForm(f => ({ ...f, requestedAmount: e.target.value }))
                              }
                              placeholder="Requested Amount"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editBifForm.jobCreation || ''}
                              onChange={e =>
                                setEditBifForm(f => ({ ...f, jobCreation: e.target.value }))
                              }
                              placeholder="Jobs Created"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editBifForm.status || ''}
                              onChange={e =>
                                setEditBifForm(f => ({ ...f, status: e.target.value }))
                              }
                              placeholder="Status"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setShowEditBif(false)}
                              className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                            >
                              Save
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}

            {activeTab === 'clients' && (
              <>
                <LiveTable
                  title="Clients Management"
                  data={data.clients}
                  columns={tableConfigs.clients.columns}
                  filters={{
                    status: filters.status || '',
                    organization: filters.organization || '',
                  }}
                  searchTerm={searchTerms.clients || ''}
                  onSearchChange={value => setSearchTerms(prev => ({ ...prev, clients: value }))}
                  onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
                  onExport={data => exportToCSV(data, 'clients')}
                  onView={handleViewClient}
                  onEdit={handleEditClient}
                  onDelete={handleDeleteClient}
                  loading={loading}
                />
                <AnimatePresence>
                  {showClientModal && selectedClient && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
                      >
                        <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-sm">
                              {(selectedClient.firstname ||
                                selectedClient.lastname ||
                                selectedClient.id)?.[0]?.toUpperCase() || 'C'}
                            </div>
                            <div>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-base font-semibold text-black">
                                  {selectedClient.firstname || selectedClient.lastname || '-'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {selectedClient.email}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                            onClick={() => setShowClientModal(false)}
                            aria-label="Close"
                          >
                            <HiXMark className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="space-y-8 p-6">
                          <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                            Client Details
                          </div>
                          <div className="grid grid-cols-1 gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUser className="h-4 w-4 text-blue-500" /> First Name:{' '}
                                <span className="font-semibold text-black">
                                  {selectedClient.firstname || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUser className="h-4 w-4 text-blue-500" /> Last Name:{' '}
                                <span className="font-semibold text-black">
                                  {selectedClient.lastname || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiEnvelope className="h-4 w-4 text-blue-500" /> Email:{' '}
                                <span className="font-semibold text-black">
                                  {selectedClient.email || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiPhone className="h-4 w-4 text-blue-500" /> Phone:{' '}
                                <span className="font-semibold text-black">
                                  {selectedClient.phone || '-'}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBuildingOffice2 className="h-4 w-4 text-blue-500" />{' '}
                                Organization:{' '}
                                <span className="font-semibold text-black">
                                  {selectedClient.organization || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiCheckCircle className="h-4 w-4 text-blue-500" /> Status:{' '}
                                <span className="font-semibold text-black">
                                  {selectedClient.status || '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditClientForm(selectedClient);
                                setShowEditClient(true);
                              }}
                              className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700"
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteClient(selectedClient)}
                              className="rounded-lg bg-red-100 px-4 py-2 text-red-800 shadow hover:bg-red-200"
                            >
                              Delete
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Edit Client Modal (simple form) */}
                <AnimatePresence>
                  {showEditClient && editClientForm && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
                      >
                        <h2 className="mb-4 text-lg font-bold">Edit Client</h2>
                        <form
                          onSubmit={async e => {
                            e.preventDefault();
                            await updateDoc(
                              doc(firestore, 'clients', editClientForm.id),
                              editClientForm
                            );
                            setData(prev => ({
                              ...prev,
                              clients: prev.clients.map(c =>
                                c.id === editClientForm.id ? editClientForm : c
                              ),
                            }));
                            setShowEditClient(false);
                          }}
                          className="space-y-4"
                        >
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <input
                              className="rounded border px-3 py-2"
                              value={editClientForm.firstname || ''}
                              onChange={e =>
                                setEditClientForm(f => ({ ...f, firstname: e.target.value }))
                              }
                              placeholder="First Name"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editClientForm.lastname || ''}
                              onChange={e =>
                                setEditClientForm(f => ({ ...f, lastname: e.target.value }))
                              }
                              placeholder="Last Name"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editClientForm.email || ''}
                              onChange={e =>
                                setEditClientForm(f => ({ ...f, email: e.target.value }))
                              }
                              placeholder="Email"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editClientForm.phone || ''}
                              onChange={e =>
                                setEditClientForm(f => ({ ...f, phone: e.target.value }))
                              }
                              placeholder="Phone"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editClientForm.organization || ''}
                              onChange={e =>
                                setEditClientForm(f => ({ ...f, organization: e.target.value }))
                              }
                              placeholder="Organization"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editClientForm.status || ''}
                              onChange={e =>
                                setEditClientForm(f => ({ ...f, status: e.target.value }))
                              }
                              placeholder="Status"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setShowEditClient(false)}
                              className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                            >
                              Save
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}

            {activeTab === 'coaches' && (
              <>
                <LiveTable
                  title="Coaches Management"
                  data={data.coaches}
                  columns={tableConfigs.coaches.columns}
                  filters={{
                    status: filters.status || '',
                    specialization: filters.specialization || '',
                  }}
                  searchTerm={searchTerms.coaches || ''}
                  onSearchChange={value => setSearchTerms(prev => ({ ...prev, coaches: value }))}
                  onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
                  onExport={data => exportToCSV(data, 'coaches')}
                  onView={handleViewCoach}
                  onEdit={handleEditCoach}
                  onDelete={handleDeleteCoach}
                  loading={loading}
                />
                <AnimatePresence>
                  {showCoachModal && selectedCoach && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
                      >
                        <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-sm">
                              {(selectedCoach.name || selectedCoach.id)?.[0]?.toUpperCase() || 'C'}
                            </div>
                            <div>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-base font-semibold text-black">
                                  {selectedCoach.name || '-'}
                                </span>
                                <span className="text-xs text-gray-500">{selectedCoach.email}</span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                            onClick={() => setShowCoachModal(false)}
                            aria-label="Close"
                          >
                            <HiXMark className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="space-y-8 p-6">
                          <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                            Coach Details
                          </div>
                          <div className="grid grid-cols-1 gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUser className="h-4 w-4 text-blue-500" /> Name:{' '}
                                <span className="font-semibold text-black">
                                  {selectedCoach.name || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiEnvelope className="h-4 w-4 text-blue-500" /> Email:{' '}
                                <span className="font-semibold text-black">
                                  {selectedCoach.email || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBriefcase className="h-4 w-4 text-blue-500" /> Role:{' '}
                                <span className="font-semibold text-black">
                                  {selectedCoach.role || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiAcademicCap className="h-4 w-4 text-blue-500" /> Specialization:{' '}
                                <span className="font-semibold text-black">
                                  {selectedCoach.specialization || '-'}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiCheckCircle className="h-4 w-4 text-blue-500" /> Status:{' '}
                                <span className="font-semibold text-black">
                                  {selectedCoach.status || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiCalendar className="h-4 w-4 text-blue-500" /> Created:{' '}
                                <span className="font-semibold text-black">
                                  {selectedCoach.createdAt
                                    ? new Date(
                                        selectedCoach.createdAt.toDate()
                                      ).toLocaleDateString()
                                    : '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditCoachForm(selectedCoach);
                                setShowEditCoach(true);
                              }}
                              className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700"
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteCoach(selectedCoach)}
                              className="rounded-lg bg-red-100 px-4 py-2 text-red-800 shadow hover:bg-red-200"
                            >
                              Delete
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Edit Coach Modal (simple form) */}
                <AnimatePresence>
                  {showEditCoach && editCoachForm && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
                      >
                        <h2 className="mb-4 text-lg font-bold">Edit Coach</h2>
                        <form
                          onSubmit={async e => {
                            e.preventDefault();
                            await updateDoc(
                              doc(firestore, 'coaches', editCoachForm.id),
                              editCoachForm
                            );
                            setData(prev => ({
                              ...prev,
                              coaches: prev.coaches.map(c =>
                                c.id === editCoachForm.id ? editCoachForm : c
                              ),
                            }));
                            setShowEditCoach(false);
                          }}
                          className="space-y-4"
                        >
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <input
                              className="rounded border px-3 py-2"
                              value={editCoachForm.name || ''}
                              onChange={e =>
                                setEditCoachForm(f => ({ ...f, name: e.target.value }))
                              }
                              placeholder="Name"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editCoachForm.email || ''}
                              onChange={e =>
                                setEditCoachForm(f => ({ ...f, email: e.target.value }))
                              }
                              placeholder="Email"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editCoachForm.role || ''}
                              onChange={e =>
                                setEditCoachForm(f => ({ ...f, role: e.target.value }))
                              }
                              placeholder="Role"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editCoachForm.specialization || ''}
                              onChange={e =>
                                setEditCoachForm(f => ({ ...f, specialization: e.target.value }))
                              }
                              placeholder="Specialization"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editCoachForm.status || ''}
                              onChange={e =>
                                setEditCoachForm(f => ({ ...f, status: e.target.value }))
                              }
                              placeholder="Status"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setShowEditCoach(false)}
                              className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                            >
                              Save
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}

            {activeTab === 'moderators' && (
              <>
                <LiveTable
                  title="Business Advisors Management"
                  data={data.moderators}
                  columns={tableConfigs.moderators.columns}
                  filters={{ status: filters.status || '', company: filters.company || '' }}
                  searchTerm={searchTerms.moderators || ''}
                  onSearchChange={value => setSearchTerms(prev => ({ ...prev, moderators: value }))}
                  onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
                  onExport={data => exportToCSV(data, 'moderators')}
                  onView={handleViewModerator}
                  onEdit={handleEditModerator}
                  onDelete={handleDeleteModerator}
                  loading={loading}
                />
                <AnimatePresence>
                  {showModeratorModal && selectedModerator && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
                      >
                        <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-sm">
                              {(selectedModerator.firstname ||
                                selectedModerator.lastname ||
                                selectedModerator.id)?.[0]?.toUpperCase() || 'M'}
                            </div>
                            <div>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-base font-semibold text-black">
                                  {selectedModerator.firstname || selectedModerator.lastname || '-'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {selectedModerator.email}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                            onClick={() => setShowModeratorModal(false)}
                            aria-label="Close"
                          >
                            <HiXMark className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="space-y-8 p-6">
                          <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                            Moderator Details
                          </div>
                          <div className="grid grid-cols-1 gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUser className="h-4 w-4 text-blue-500" /> First Name:{' '}
                                <span className="font-semibold text-black">
                                  {selectedModerator.firstname || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiUser className="h-4 w-4 text-blue-500" /> Last Name:{' '}
                                <span className="font-semibold text-black">
                                  {selectedModerator.lastname || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiEnvelope className="h-4 w-4 text-blue-500" /> Email:{' '}
                                <span className="font-semibold text-black">
                                  {selectedModerator.email || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBuildingOffice2 className="h-4 w-4 text-blue-500" /> Company:{' '}
                                <span className="font-semibold text-black">
                                  {selectedModerator.company || '-'}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiBriefcase className="h-4 w-4 text-blue-500" /> Department:{' '}
                                <span className="font-semibold text-black">
                                  {selectedModerator.department || '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <HiCheckCircle className="h-4 w-4 text-blue-500" /> Status:{' '}
                                <span className="font-semibold text-black">
                                  {selectedModerator.status || '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setEditModeratorForm(selectedModerator);
                                setShowEditModerator(true);
                              }}
                              className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700"
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteModerator(selectedModerator)}
                              className="rounded-lg bg-red-100 px-4 py-2 text-red-800 shadow hover:bg-red-200"
                            >
                              Delete
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Edit Moderator Modal (simple form) */}
                <AnimatePresence>
                  {showEditModerator && editModeratorForm && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="relative w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
                      >
                        <h2 className="mb-4 text-lg font-bold">Edit Moderator</h2>
                        <form
                          onSubmit={async e => {
                            e.preventDefault();
                            await updateDoc(
                              doc(firestore, 'moderator', editModeratorForm.id),
                              editModeratorForm
                            );
                            setData(prev => ({
                              ...prev,
                              moderators: prev.moderators.map(m =>
                                m.id === editModeratorForm.id ? editModeratorForm : m
                              ),
                            }));
                            setShowEditModerator(false);
                          }}
                          className="space-y-4"
                        >
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <input
                              className="rounded border px-3 py-2"
                              value={editModeratorForm.firstname || ''}
                              onChange={e =>
                                setEditModeratorForm(f => ({ ...f, firstname: e.target.value }))
                              }
                              placeholder="First Name"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editModeratorForm.lastname || ''}
                              onChange={e =>
                                setEditModeratorForm(f => ({ ...f, lastname: e.target.value }))
                              }
                              placeholder="Last Name"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editModeratorForm.email || ''}
                              onChange={e =>
                                setEditModeratorForm(f => ({ ...f, email: e.target.value }))
                              }
                              placeholder="Email"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editModeratorForm.company || ''}
                              onChange={e =>
                                setEditModeratorForm(f => ({ ...f, company: e.target.value }))
                              }
                              placeholder="Company"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editModeratorForm.department || ''}
                              onChange={e =>
                                setEditModeratorForm(f => ({ ...f, department: e.target.value }))
                              }
                              placeholder="Department"
                            />
                            <input
                              className="rounded border px-3 py-2"
                              value={editModeratorForm.status || ''}
                              onChange={e =>
                                setEditModeratorForm(f => ({ ...f, status: e.target.value }))
                              }
                              placeholder="Status"
                            />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setShowEditModerator(false)}
                              className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                            >
                              Save
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}

            {activeTab === 'tasks' && <AdminTaskManager />}
          </motion.div>
        </AnimatePresence>
        {/* Add state for application modal */}
        <AnimatePresence>
          {showApplicationModal && selectedApplication && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
              >
                <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-sm">
                      {selectedApplication.business?.businessName?.[0]?.toUpperCase() || 'A'}
                    </div>
                    <div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-base font-semibold text-black">
                          {selectedApplication.business?.businessName || '-'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {selectedApplication.userEmail}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                    onClick={() => setShowApplicationModal(false)}
                    aria-label="Close"
                  >
                    <HiXMark className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-8 p-6">
                  <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                    Application Details
                  </div>
                  <div className="grid grid-cols-1 gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Applicant ID:</span>{' '}
                        {selectedApplication.applicantId || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Status:</span>{' '}
                        {selectedApplication.status || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Completed At:</span>{' '}
                        {selectedApplication.completedAt
                          ? typeof selectedApplication.completedAt.toDate === 'function'
                            ? new Date(selectedApplication.completedAt.toDate()).toLocaleString()
                            : new Date(selectedApplication.completedAt).toLocaleString()
                          : '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">User Email:</span>{' '}
                        {selectedApplication.userEmail || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">User ID:</span>{' '}
                        {selectedApplication.userId || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Client ID:</span>{' '}
                        {selectedApplication.clientId || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Business Name:</span>{' '}
                        {selectedApplication.business?.businessName || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Industry:</span>{' '}
                        {selectedApplication.business?.industry || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Annual Revenue:</span>{' '}
                        {selectedApplication.business?.annualRevenue || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Employee Count:</span>{' '}
                        {selectedApplication.business?.employeeCount || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Registration Number:</span>{' '}
                        {selectedApplication.business?.registrationNumber || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="font-semibold">Years in Operation:</span>{' '}
                        {selectedApplication.business?.yearsInOperation || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={
          deleteConfirm.type === 'alumni'
            ? 'Delete Alumni'
            : deleteConfirm.type === 'bif'
              ? 'Delete BIF Application'
              : deleteConfirm.type === 'client'
                ? 'Delete Client'
                : deleteConfirm.type === 'coach'
                  ? 'Delete Coach'
                  : deleteConfirm.type === 'moderator'
                    ? 'Delete Moderator'
                    : 'Delete Item'
        }
        message={
          deleteConfirm.type === 'alumni'
            ? `Are you sure you want to delete this alumni? This action cannot be undone.`
            : deleteConfirm.type === 'bif'
              ? `Are you sure you want to delete this BIF application? This action cannot be undone.`
              : deleteConfirm.type === 'client'
                ? `Are you sure you want to delete this client? This action cannot be undone.`
                : deleteConfirm.type === 'coach'
                  ? `Are you sure you want to delete this coach? This action cannot be undone.`
                  : deleteConfirm.type === 'moderator'
                    ? `Are you sure you want to delete this moderator? This action cannot be undone.`
                    : `Are you sure you want to delete ${deleteConfirm.item?.firstname || deleteConfirm.item?.businessName || deleteConfirm.item?.companyName || deleteConfirm.item?.name || 'this item'}? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AdminCRM;
